import { DateTime } from "luxon";

export type TimeGranularity = "day" | "week" | "month"; // if we add a "today" view, add "hour"

//
export interface TimePeriod {
  granularity: TimeGranularity;
  start: DateTime;
  end: DateTime;
}

/**
 * Given a time range (start to end), break it up in `n` periods according
 * to the specified granularity.
 *
 * For example, given a time range that is ~1yr long:
 *  - if granularity = "day", should break up into ~365 days
 *  - if granularity = "week", should break up into ~52 weeks
 *  - if granularity = "month", should break up into 12 months
 */
export const convertDateRangeToTimePeriods = (
  granularity: TimeGranularity,
  rangeStart: DateTime,
  rangeEnd: DateTime,
): TimePeriod[] => {
  const periods: TimePeriod[] = [];
  // Convert to UTC and normalize to start of day
  let currentStart = rangeStart.toUTC().startOf(granularity);
  const end = rangeEnd.toUTC().startOf(granularity);

  while (currentStart <= end) {
    const periodEnd = currentStart.endOf(granularity);
    periods.push({
      granularity,
      start: currentStart,
      end: periodEnd,
    });
    currentStart = currentStart.plus({ [`${granularity}s`]: 1 });
  }

  return periods;
};

const IDEAL_MIN_NUM_COLUMNS = 3;

export const chooseBestGranularity = (
  rangeStart: DateTime,
  rangeEnd: DateTime,
): TimeGranularity => {
  const opts: TimeGranularity[] = ["month", "week", "day"];

  // since it's descending, finds largest unit that will result in enough data points in chart
  const bestFit = opts.find((gran) => {
    const numUnitsInGranularity = rangeEnd.diff(rangeStart, gran)[`${gran}s`];
    return numUnitsInGranularity >= IDEAL_MIN_NUM_COLUMNS;
  });

  return bestFit ?? "day";
};

// export const getTimePeriodLabel = (
//   t: TFunction,
//   rangeStart: DateTime,
//   rangeEnd: DateTime | null,
// ): string => {
//   const now = DateTime.local().startOf("day");

//   const rangeEndOrDefault = rangeEnd ?? now;

//   const diffMonths = rangeEndOrDefault.diff(rangeStart, "months").months;
//   const diffDays = rangeEndOrDefault.diff(rangeStart, "days").days;

//   // const isRelative = rangeEnd.startOf("day").equals(now.startOf("day"));
//   const isRelative = !rangeEnd;
//   console.log(rangeStart.toISO(), rangeEnd?.toISO(), diffDays, isRelative);

//   if (isRelative) {
//     return diffMonths >= 1
//       ? t("dateRangePicker.lastNMonths", { n: diffMonths })
//       : t("dateRangePicker.lastNDays", { n: diffDays });
//   }

//   const opts: TimeGranularity[] = ["day", "month"];
//   const bestFit = opts.find((gran) => {
//     const numUnitsInGranularity = rangeEnd.diff(rangeStart, gran)[`${gran}s`];
//     return numUnitsInGranularity <= 1;
//   });

//   console.log(rangeStart.toISO(), rangeEnd.toISO(), bestFit);

//   // const isOneMonth = Math.round(diffMonths) === 1

//   const isOneDay = Math.round(diffDays) === 1;
//   if (isOneDay && rangeStart.day === now.day) {
//     return t("dateRangePicker.today");
//   }
//   if (isOneDay && rangeStart.day === now.minus({ days: 1 }).day) {
//     return t("dateRangePicker.yesterday");
//   }
//   if (diffDays <= 31) {
//     return t("dateRangePicker.lastNDays", { n: Math.round(diffDays) });
//   }

//   // const diffMonths = Math.round(rangeEnd.diff(rangeStart, "months").months);
//   // const isOneMonth = diffMonths === 1;
//   // if (isOneMonth && rangeStart.month === now.month) {
//   //   return t("dateRangePicker.thisMonth");
//   // }
//   // if (isOneMonth && rangeStart.month === now.minus({ months: 1 }).month) {
//   //   return t("dateRangePicker.lastMonth");
//   // }

//   // if (diffMonths <= 12) {
//   //   return t("dateRangePicker.lastNMonths", { n: Math.round(diffMonths) });
//   // }

//   return `${rangeStart.toFormat("yyyy-MM-dd")} through ${rangeEndOrDefault.toFormat("yyyy-MM-dd")}`;
// };

const hashTimePeriodForTimeCompare = (tp: TimePeriod): string => {
  return `${tp.start.toFormat("yyyy-MM-dd")}..${tp.end.toFormat("yyyy-MM-dd")}`;
};

export const timePeriodMatches = (
  tp1: TimePeriod,
  tp2: TimePeriod,
): boolean => {
  return (
    hashTimePeriodForTimeCompare(tp1) === hashTimePeriodForTimeCompare(tp2)
  );
};
